import { useCallback, useEffect, useRef, useState } from "react"

import type { Elemento, Flujo, Paso } from "./tipos"

/**
 * La guía, en voz alta (28/10, pedido del usuario: «instrucciones por voz de las funciones
 * a realizar»).
 *
 * ⚠ **La voz es la del NAVEGADOR** (Web Speech API, `speechSynthesis`): no hay servidor,
 * ni costo, ni audio grabado que se quede viejo cuando cambie un botón — se lee el mismo
 * texto que se ve. Sin soporte (algún navegador viejo), el botón no se ofrece.
 */

/** Cómo se dice un elemento de la réplica. */
export function decir(e: Elemento): string {
  switch (e.tipo) {
    case "ruta":
      return `Entra a ${e.pasos.join(", luego ")}.`
    case "pestanas":
      return `Abre la pestaña ${e.activa}.`
    case "campo":
      return `En ${e.etiqueta}, escribe por ejemplo: ${e.valor}.`
    case "lista":
      return `En ${e.etiqueta}, elige ${e.valor}.`
    case "boton":
      return `Pulsa ${e.texto}.`
    case "nota":
      return e.tono === "aviso" ? `Atención: ${e.texto}` : e.texto
  }
}

/** El texto de un paso, como se lee: su título, su explicación y sus elementos. */
export function textoDePaso(p: Paso, n: number): string {
  return [`Paso ${n}. ${p.titulo}.`, p.texto, ...(p.elementos ?? []).map(decir)].join(" ")
}

/** Lo que se lee de un flujo, por partes: la introducción y luego un trozo por paso. */
export function guionDe(f: Flujo): string[] {
  const intro = [`${f.titulo}.`, `Cuándo: ${f.cuando}`]
  if (f.antes?.length) intro.push(`Antes necesitas: ${f.antes.join("; ")}.`)
  return [
    intro.join(" "),
    ...f.pasos.map((p, i) => textoDePaso(p, i + 1)),
    `Resultado: ${f.resultado}${f.ojo ? ` Importante: ${f.ojo}` : ""}`,
  ]
}

export const hayVoz = (): boolean => typeof window !== "undefined" && "speechSynthesis" in window

function vozEnEspanol(): SpeechSynthesisVoice | undefined {
  const voces = window.speechSynthesis.getVoices()
  return (
    voces.find((v) => v.lang.toLowerCase().startsWith("es-ve")) ??
    voces.find((v) => v.lang.toLowerCase().startsWith("es-419")) ??
    voces.find((v) => v.lang.toLowerCase().startsWith("es"))
  )
}

/**
 * Lee un flujo trozo a trozo y dice cuál va (`actual`: -1 la introducción, 0..n-1 los
 * pasos, n el resultado; `null` callado). Un solo flujo a la vez: empezar otro corta el
 * anterior.
 */
export function useLectura(velocidad = 1) {
  const [leyendo, setLeyendo] = useState<string | null>(null)
  const [actual, setActual] = useState<number | null>(null)
  const [pausado, setPausado] = useState(false)
  const turno = useRef(0)

  const parar = useCallback(() => {
    turno.current += 1
    if (hayVoz()) window.speechSynthesis.cancel()
    setLeyendo(null)
    setActual(null)
    setPausado(false)
  }, [])

  const leer = useCallback(
    (f: Flujo, desde = 0) => {
      if (!hayVoz()) return
      window.speechSynthesis.cancel()
      const mio = ++turno.current
      const partes = guionDe(f)
      const voz = vozEnEspanol()
      setLeyendo(f.id)
      setPausado(false)
      const decirParte = (i: number) => {
        if (turno.current !== mio) return
        if (i >= partes.length) {
          setLeyendo(null)
          setActual(null)
          return
        }
        const u = new SpeechSynthesisUtterance(partes[i])
        u.lang = voz?.lang ?? "es-ES"
        if (voz) u.voice = voz
        u.rate = velocidad
        u.onstart = () => turno.current === mio && setActual(i - 1)
        u.onend = () => decirParte(i + 1)
        window.speechSynthesis.speak(u)
      }
      decirParte(desde)
    },
    [velocidad],
  )

  const alternarPausa = useCallback(() => {
    if (!hayVoz()) return
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume()
      setPausado(false)
    } else {
      window.speechSynthesis.pause()
      setPausado(true)
    }
  }, [])

  // Al salir de la guía, que no siga hablando.
  useEffect(() => parar, [parar])

  return { leyendo, actual, pausado, leer, parar, alternarPausa }
}
